import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";

/**
 * Offer status enum
 */
export const OfferStatusSchema = z.enum(["pending", "completed", "error"]);
export const OfferLifecycleStatusSchema = z.enum([
  "pending",
  "completed",
  "error",
  "archived",
  "implemented",
]);
export type OfferStatus = z.infer<typeof OfferStatusSchema>;

/**
 * Offer context schema
 */
export const OfferContextSchema = z.object({
  offer_name: z.string().default(""),
  business_model: z.string().default(""),
  pricing_details: z.string().default(""),
  positioning: z.string().default(""),
});
export type OfferContext = z.infer<typeof OfferContextSchema>;

/**
 * Value messaging schema
 */
export const ValueMessagingSchema = z.object({
  primary_value_proposition: z.string().default(""),
  key_benefits: z.array(z.string()).default([]),
  package_inclusions: z.array(z.string()).default([]),
});
export type ValueMessaging = z.infer<typeof ValueMessagingSchema>;

/**
 * Conversion elements schema
 */
export const ConversionElementsSchema = z.object({
  conversion_goals: z.array(z.string()).default([]),
  social_proof: z.array(z.string()).default([]),
  pricing_incentives: z.array(z.string()).default([]),
  target_use_cases: z.array(z.string()).default([]),
});
export type ConversionElements = z.infer<typeof ConversionElementsSchema>;

/**
 * Offer document schema (without id)
 * Path: organizations/{orgId}/domains/{domainId}/offers/{offerId}
 * Note: Timestamps are converted to Date in the store
 */
export const OfferDocSchema = z.object({
  user_id: z.string(),
  domain_id: z.string(),
  page_id: z.string(),
  is_enabled: z.boolean(),
  status: OfferLifecycleStatusSchema.default("pending"),
  offer_context: OfferContextSchema.nullable().default(null),
  value_messaging: ValueMessagingSchema.nullable().default(null),
  conversion_elements: ConversionElementsSchema.nullable().default(null),
  custom_note: z.string().default(""),
  error_message: z.string().optional(),
  created_at: RequiredFirestoreTimestampSchema,
  // Which conversation turn produced this document, when the assistant made it.
  // Backend-written only (these collections are `allow write: if false`), so the
  // value is trustworthy; null on anything a member made by hand and on
  // documents written before the fields existed.
  source_thread_id: z.string().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
});
export type OfferDoc = z.infer<typeof OfferDocSchema>;

/**
 * Offer schema (with id)
 */
export const OfferSchema = OfferDocSchema.extend({ id: z.string() });
export const OfferEntitySchema = OfferSchema.extend({
  updated_at: RequiredFirestoreTimestampSchema.nullable().optional(),
});
export type Offer = z.infer<typeof OfferEntitySchema>;

/**
 * Parse functions
 */
export const parseOffer = (data: unknown): Offer =>
  OfferEntitySchema.parse(data);
export const safeOfferParse = (data: unknown) =>
  OfferEntitySchema.safeParse(data);

// UI State type (not a Zod schema - internal app state)
export interface OfferState {
  offers: Offer[];
  selectedOfferId: string | null;
  isLoading: boolean;
  error: string | null;
}
